import { lazy, Suspense, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { LogOut } from 'lucide-react';
import Logout from './logout';
import { useUserContext } from '@/contexts/UserContext';
import { useQueryClient } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import { clientThreadsQueryOptions } from '@/domains/threads/api/clientThreadsQueryOptions';

// Lazy-load page components for code splitting
const OutreachMetrics = lazy(() => import('@/features/dashboard/OutreachMetrics'));
const CRM = lazy(() => import('@/features/crm/CRM'));
const Settings = lazy(() => import('@/features/settings/Settings'));

// Map URL paths to the old activePage IDs (used by Sidebar)
const pathToPageId = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/crm': 'crm',
  '/settings': 'settings',
  '/logoff': 'logoff',
};

// Map old page IDs to URL paths (used for navigation)
const pageIdToPath = {
  'dashboard': '/',
  'crm': '/crm',
  'settings': '/settings',
  'logoff': '/logoff',
};

const PageFallback = () => (
  <div className="flex items-center justify-center h-full">
    <ClipLoader size={50} color={"#facc15"} />
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId, client } = useUserContext();
  const queryClient = useQueryClient();

  const logout = Logout();
  // Derive activePage from current URL path for Sidebar highlighting
  const activePage = pathToPageId[location.pathname] || 'dashboard';

  // Bridge: Sidebar calls onPageChange(id) → we navigate to the URL
  const handlePageChange = useCallback((pageId) => {
    const path = pageIdToPath[pageId] || '/';
    navigate(path);
  }, [navigate]);

  useEffect(() => {
    if (!clientId || !client) return;

    // CRM and prefetching share one complete query contract and cache key.
    queryClient.prefetchQuery({
      ...clientThreadsQueryOptions(clientId),
      staleTime: 2 * 60 * 1000,
    });
  }, [clientId, client, queryClient]);

  return (
    <div className="flex h-screen relative overflow-hidden" style={{
      background: 'radial-gradient(ellipse 140% 90% at -5% 110%, rgba(40,20,0,0.55) 0%, transparent 55%), radial-gradient(ellipse 100% 70% at 105% -5%, rgba(30,15,0,0.35) 0%, transparent 55%), radial-gradient(ellipse 80% 60% at 50% 110%, rgba(24,12,0,0.25) 0%, transparent 60%), #080808'
    }}>
      <Sidebar activePage={activePage} onPageChange={handlePageChange} />

      <div className="flex-1 flex flex-col overflow-hidden relative">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Dashboard / Home */}
            <Route path="/" element={
              <div className="flex-grow bg-transparent overflow-y-auto p-6">
                <div className="max-w-7xl mx-auto">
                  <OutreachMetrics />
                </div>
              </div>
            } />

            {/* CRM */}
            <Route path="/crm" element={
              <div className="flex-grow min-w-0 bg-transparent overflow-y-auto p-2 sm:p-6">
                <div className="max-w-7xl mx-auto">
                  <CRM />
                </div>
              </div>
            } />

            {/* Settings */}
            <Route path="/settings" element={
              <div className="flex-grow bg-transparent overflow-y-auto">
                <Settings />
              </div>
            } />

            {/* Logoff */}
            <Route path="/logoff" element={
              <div className="flex-grow bg-transparent flex justify-center items-center p-6">
                <div className="bg-[#161616] border border-[#222222] rounded-2xl p-8 max-w-sm w-full">
                  <div className="text-center">
                    <div className="w-14 h-14 bg-[#181818] border border-[#252525] rounded-xl flex items-center justify-center mx-auto mb-6">
                      <LogOut className="w-6 h-6 text-[#F59E0B]" />
                    </div>
                    <h2 className="text-lg font-semibold text-[#ccc] mb-2">Confirm Logout</h2>
                    <p className="text-sm text-[#555] mb-8">Are you sure you want to log out?</p>
                    <button
                      onClick={logout}
                      className="w-full bg-[#D97706] hover:bg-[#B45309] text-[#080808] font-semibold py-3 px-6 rounded-xl transition-colors duration-150 cursor-pointer"
                    >
                      Confirm Logout
                    </button>
                  </div>
                </div>
              </div>
            } />

            {/* Catch-all: redirect unknown paths to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};

export default Dashboard;
